import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { isFuture } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  IOsAppFeature,
  NotIOsAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { useEarlyAccessRefinement } from '@sorare/core/src/hooks/search/useEarlyAccess';
import { glossary } from '@sorare/core/src/lib/glossary';

import { PromoHeader } from 'components/earlyAccess/PromoHeader';
import EarlyAccessDialog from 'components/market/EarlyAccessDialog';
import { EarlyAccessSpecialOfferBannerContent } from 'components/market/EarlyAccessSpecialOfferBannerContent';
import { earlyAccessEvents } from 'lib/earlyAccess';

import card from './assets/card.png';
import essence from './assets/essence.png';
import mbappe from './assets/mbappe.png';

const Wrapper = styled(ButtonBase)`
  background-color: var(--c-nd-100);
  padding: var(--intermediate-unit);
  border-radius: var(--unit);
  justify-content: center;
  text-align: center;
  &:hover {
    background-color: var(--c-nd-50);
  }
`;

const Underline = styled(BodyM)`
  text-decoration: underline;
`;

const Coloured = styled(BodyM)`
  background: linear-gradient(
    92deg,
    #fff 0%,
    #eabfe2 28.88%,
    #fff 48.67%,
    #bedecb 64.58%,
    #fff 100%
  );

  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const IOsBlock = styled(ButtonBase)`
  background-color: var(--c-nd-100);
  border-radius: var(--unit);
  overflow: hidden;
`;
const IOsContent = styled(Horizontal)`
  padding: var(--intermediate-unit);
  overflow-y: auto;
`;
const IconCtn = styled(Horizontal).attrs({ center: true })`
  padding: var(--half-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-100);
`;

const BannerBg = styled(Vertical)`
  background-color: var(--c-nd-100);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
`;
const Icon = styled.img`
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
`;
const StyledChip = styled(Chip)`
  color: var(--c-black);
  background: var(--c-nd-200);
`;

export const EarlyAccessInfos = () => {
  const { enabledEarlyAccess } = useEarlyAccessRefinement();
  const enabledEarlyAccessEvent = earlyAccessEvents.find(
    e => e.searchParams === enabledEarlyAccess?.name
  );
  const [open, setOpen] = useState(false);
  if (!enabledEarlyAccess) {
    return null;
  }
  return (
    <>
      <IOsAppFeature>
        {enabledEarlyAccessEvent?.endDate &&
          isFuture(enabledEarlyAccessEvent?.endDate) && (
            <>
              <IOsBlock onClick={() => setOpen(true)}>
                <PromoHeader
                  endDate={enabledEarlyAccessEvent!.endDate}
                  desc={
                    <BodyS bold color="var(--c-black)">
                      <FormattedMessage
                        id="search.earlyAccess.ios.promo"
                        defaultMessage="For each purchase, get either"
                      />
                    </BodyS>
                  }
                />
                <IOsContent>
                  <StyledChip>
                    <IconCtn>
                      <Icon src={card} />
                    </IconCtn>
                    <Coloured>
                      <FormattedMessage
                        id="search.earlyAccess.ios.bonusCard"
                        defaultMessage="1 bonus Card"
                      />
                    </Coloured>
                  </StyledChip>
                  <StyledChip>
                    <IconCtn>
                      <Icon src={essence} />
                    </IconCtn>
                    <Coloured>
                      <FormattedMessage
                        id="search.earlyAccess.ios.essence"
                        defaultMessage="Essences"
                      />
                    </Coloured>
                  </StyledChip>
                  <StyledChip>
                    <IconCtn>
                      <Icon src={mbappe} />
                    </IconCtn>
                    <Coloured>
                      <FormattedMessage
                        id="search.earlyAccess.ios.mbappe"
                        defaultMessage="A chance to win 1 special Mbappé Rare Card"
                      />
                    </Coloured>
                  </StyledChip>
                </IOsContent>
              </IOsBlock>
              <Drawer
                blur
                open={open}
                side="bottom"
                onBackdropClick={() => setOpen(false)}
              >
                <BannerBg gap={2}>
                  <EarlyAccessSpecialOfferBannerContent
                    event={enabledEarlyAccessEvent!}
                    rarities={[Rarity.limited]}
                  />
                  <Button
                    onClick={() => setOpen(false)}
                    size="medium"
                    color="secondary"
                  >
                    <FormattedMessage {...glossary.gotIt} />
                  </Button>
                </BannerBg>
              </Drawer>
            </>
          )}
      </IOsAppFeature>
      <NotIOsAppFeature>
        <Wrapper onClick={() => setOpen(true)}>
          <Horizontal gap={1} center>
            <FontAwesomeIcon icon={faInfoCircle} />
            <Coloured bold>
              <FormattedMessage
                id="search.earlyAccess.infos"
                defaultMessage="Early Access are playable now"
              />
            </Coloured>
            <Underline>
              <FormattedMessage {...glossary.more} />
            </Underline>
            <ChevronRightBold />
          </Horizontal>
        </Wrapper>
        <EarlyAccessDialog open={open} onClose={() => setOpen(false)} />
      </NotIOsAppFeature>
    </>
  );
};
